import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useCallback,
  useMemo,
} from 'react';

import { CheckThick } from '../../assets/js/svg/gui';
import { appTooltipProps } from '../../utils';
import { Row } from '../display';
import SvgColorImg from '../SvgColorImg';

function Checkbox(props) {
  const {
    // static, changes are ignored:
    id,
    name,
    // dynamic (changes are reflected):
    className,
    onChange, // set to true to prevent box from looking disabled
    checked,
    partial,
    disabled,
    label,
    placeHolder,
    color,
    src,
    hintText,
    gap = 0,
    fillParentDiv,
  } = props;

  const handleClick = useCallback(
    () => {
      if (onChange !== true) {
        onChange({
          currentTarget: {
            id,
            name,
            value: !checked,
          },
        });
      }
    },
    [id, name, onChange, checked],
  );

  const checkboxContent = useMemo(
    () => (
      <div
        className={classNames(
          'w-full h-full border-2 rounded-sm relative flex flex-column items-center justify-center',
          {
            'border-pxCheckActive': (!color) && (!disabled) && onChange,
            'border-black/50': (!color) && (disabled || (!onChange)),
          },
        )}
        style={(color) ? { borderColor: color } : undefined}
      >
        {(checked && (!src))
          ? (
              // <SvgColorImg
              //   className={classNames(
              //     'w-[90%] h-[90%]',
              //     {
              //       'fill-pxCheckActive': (!disabled) && (!color) && onChange,
              //       'opacity-60': disabled || partial || (!onChange),
              //     },
              //   )}
              //   src="/images/svg/gui/checkThick.svg"
              //   imgColor={color || undefined}
              // />
              <CheckThick
                className={classNames(
                  'w-[90%] h-[90%]',
                  {
                    'fill-pxCheckActive': (!disabled) && (!color) && onChange,
                    'opacity-60': disabled || partial || (!onChange),
                  },
                )}
                style={(color) ? { fill: color } : undefined}
              />
            )
          : null}
        {(checked && src)
          ? (
              <SvgColorImg
                className={classNames(
                  'w-[80%] h-[80%]',
                  {
                    'fill-pxCheckActive': (!disabled) && (!color) && onChange,
                    'opacity-60': disabled || partial || (!onChange),
                  },
                )}
                src={src}
                imgColorAsCssFill
                style={(color) ? { fill: color } : undefined}
              />
        // <CheckThick
        //   className={classNames(
        //     'w-[90%] h-[90%]',
        //     {
        //       'fill-pxCheckActive': (!disabled) && (!color) && onChange,
        //       'opacity-60': disabled || partial || (!onChange),
        //     },
        //   )}
        //   style={(color) ? { fill: color } : undefined}
        // />
            )
          : null}
      </div>
    ),
    [disabled, color, onChange, checked, partial, src],
  );

  if (placeHolder) {
    return (
      <div className={classNames(
        'pointer-events-none',
        className,
      )}
      >
        <div className="w-7 h-7 shrink-0" />
      </div>
    );
  }

  if (disabled || (!onChange) || (onChange === true)) {
    if (!label) {
      return (
        <div
          id={id}
          className={classNames(
            'w-7 h-7 shrink-0 p-1.5 pointer-events-none',
            className,
          )}
        >
          {checkboxContent}
        </div>
      );
    }

    return (
      <div
        id={id}
        className={className}
      >
        <Row start gap={gap}>
          <div className="w-7 h-7 shrink-0 p-1.5">
            {checkboxContent}
          </div>
          <span className="text-start">{label}</span>
        </Row>
      </div>
    );
  }

  if ((!label) && fillParentDiv) {
    return (
      <>
        <div className="w-7 h-7 shrink-0" />
        <button
          type="button"
          onClick={handleClick}
          id={id}
          className={classNames(
            'absolute inset-0 flex items-center justify-center',
            className,
          )}
          {...appTooltipProps(hintText)}
        >
          <div className="w-7 h-7 shrink-0 p-1.5">
            {checkboxContent}
          </div>
        </button>
      </>
    );
  }

  if (!label) {
    return (
      <button
        type="button"
        onClick={handleClick}
        id={id}
        className={classNames(
          'w-7 h-7 shrink-0 p-1.5',
          className,
        )}
        {...appTooltipProps(hintText)}
      >
        {checkboxContent}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      id={id}
      className={className}
      {...appTooltipProps(hintText)}
    >
      <Row start gap={gap}>
        <div className="w-7 h-7 shrink-0 p-1.5">
          {checkboxContent}
        </div>
        <span className="text-start">{label}</span>
      </Row>
    </button>
  );
}

Checkbox.propTypes = {
  className: PropTypes.string,
  id: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  name: PropTypes.string,
  onChange: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.func,
  ]),
  checked: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
  ]),
  partial: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
  placeHolder: PropTypes.bool,
  color: PropTypes.string,
  src: PropTypes.string,
  hintText: PropTypes.string,
  gap: PropTypes.number,
  // Clickable area covers the nearest positioned ancestor, e.g. a whole table cell
  fillParentDiv: PropTypes.bool,
};

export default Checkbox;
